/**
 * 브랜드 상세의 AEO(AI 검색) 콘텐츠: 요약 문단과 브랜드별 JSON-LD.
 *
 * - 요약 문단은 "브랜드명 + 카드 + 할인율 + 적용 범위"를 한 번에 답하는 문장이다. AI 검색(ChatGPT·Perplexity·구글 AI 개요·네이버 AI 브리핑)은
 *   질문("자담치킨 청구할인")에 바로 답하는 문단을 그대로 인용한다. 문장은 세 문장(결제 조건·대상 카드·중복 적용) + 짧은 적용 범위 한 문장만 둔다.
 *   검색어를 억지로 끼운 문장은 두지 않는다(2026-10-26 사용자 결정).
 * - JSON-LD 는 같은 사실을 구조화해 브랜드(엔티티)·할인(Offer)·페이지 위치(Breadcrumb)를 확정한다. 화면에는 보이지 않는다.
 * - 호스트(카드)별 문구 조각은 src/lib/siteProfiles.js 프로필(payWith·subject·mechanism·closing·audience)에서 가져온다.
 *   kbprm·narasarang 은 그 조각이 카드사 심의 문구 그대로이므로 문장 뼈대에 끼워 넣기만 하고 새 표현을 만들지 않는다.
 * - 화면(BrandSeoSummary)과 JSON-LD(setBrandJsonLd)가 같은 함수를 쓰므로 문장과 구조화 데이터가 항상 일치한다.
 */
import { MAIN_ORIGIN } from "@/lib/siteProfiles";
import { SITE_NAME, absoluteUrl, currentProfile, imageUrl, setJsonLd, stripHtml } from "@/lib/Seo";

/** 요약 문단에 매장 수를 쓰는 최소 매장 수. 이보다 적으면 범위 문장을 생략한다. */
const STORE_COUNT_MIN = 5;

/** 화면과 메타에 쓰는 카드 호칭. bridgem 은 "복지카드", 파트너 호스트는 카드명. */
export function cardLabel(profile) {
	return profile.isMain ? "복지카드" : profile.cardName;
}

/**
 * 브랜드 상세 페이지 제목(사이트명 제외).
 * - bridgem: "브랜드명 청구할인 N% 복지카드 혜택 및 매장". 브랜드명과 "청구할인"을 붙여 두어 브랜드만 치는 검색("디거앤레이스 청구할인")에서
 *   카드 호스트 페이지보다 정확한 일치가 되게 한다 (2026-10-29. 네이버는 제목 정확 일치 가중치가 커서 초기 색인에서 nhbank 목록이 대표로 잡혔음).
 * - 파트너 호스트: "브랜드명 카드명 청구할인 N% 혜택 및 매장". 브랜드 + 카드명 검색은 해당 호스트가 가장 정확한 일치가 된다.
 */
export function buildBrandTitle(brandName, benefit, profile) {
	if (!brandName) return "";
	const p = profile || currentProfile();
	const rate = benefit ? " " + benefit + "%" : "";
	return p.isMain
		? `${brandName} 청구할인${rate} 복지카드 혜택 및 매장`
		: `${brandName} ${cardLabel(p)} 청구할인${rate} 혜택 및 매장`;
}

/**
 * 브랜드 상세 요약 문단.
 * @param {Object} brand brandName, benefit, brandType, storeCnt 를 가진 객체
 * @param {Array}  cards { alt: '국민카드' } 형태의 대상 카드 배열
 * @param {Object} [profile] 호스트 프로필. 비우면 현재 호스트
 */
export function buildBrandSummary(brand, cards, profile) {
	const name = brand && brand.brandName;
	if (!name) return "";
	const p = profile || currentProfile();
	const card = cardLabel(p);
	const payWith = p.payWith || card;
	const subject = p.subject ? p.subject + " " : "";
	const benefit = brand.benefit ? brand.benefit + "% " : "";
	const mechanism = p.mechanism || "청구할인이 적용되어 익월 카드 청구 금액에서 할인됩니다.";
	let sentence = `${name}에서 ${subject}${payWith}로 결제하면 ${benefit}${mechanism}`;
	const labels = (cards || []).map((item) => item && item.alt).filter(Boolean);
	if (labels.length) {
		sentence += ` 대상 카드: ${labels.join(", ")}.`;
	}
	sentence += " " + (p.closing || "추가 가입·연회비·전월 실적 없이 기존 할인과 중복 적용됩니다.");
	// 적용 범위("어디서 쓸 수 있나"): 온라인 브랜드는 온라인 결제, 오프라인은 제휴 매장 수. 매장이 적으면(5곳 미만) 숫자를 쓰지 않는다.
	const storeCnt = Number(brand.storeCnt) || 0;
	if (brand.brandType === "BRAND_ONLINE") {
		sentence += ` ${name} 청구할인은 온라인 결제 시 적용됩니다.`;
	} else if (storeCnt >= STORE_COUNT_MIN) {
		sentence += ` 전국 ${name} 제휴 매장 ${storeCnt.toLocaleString("ko-KR")}곳에서 적용됩니다.`;
	}
	return sentence;
}

function normalizeUrl(value) {
	const text = String(value || "").trim();
	if (!text || /\s/.test(text)) return "";
	return /^https?:\/\//i.test(text) ? text : "https://" + text.replace(/^\/+/, "");
}

/**
 * 브랜드 상세 JSON-LD 를 head 에 넣는다(id: jsonld-brand). 브랜드가 없으면 제거한다.
 * 프리렌더(scripts/prerender.js)가 jsonld-* 스크립트를 스냅샷에 포함하고, 라우트 전환 시 applyRouteMeta 가 지운다.
 * @param {Object} options
 * @param {Object} options.brand    brandDetail 객체
 * @param {Array}  options.cards    대상 카드 배열
 * @param {string} options.path     상세 경로(/HFO_BI_002/<uuid>)
 * @param {string} options.listPath 브랜드 목록 경로(빵부스러기용)
 * @param {string} [options.title]  페이지 제목(사이트명 제외)
 */
export function setBrandJsonLd({ brand, cards, path, listPath, title }) {
	const name = brand && brand.brandName;
	if (!name || !path) {
		setJsonLd("brand", null);
		return;
	}
	const profile = currentProfile();
	const card = cardLabel(profile);
	const pageUrl = absoluteUrl(path);
	const summary = buildBrandSummary(brand, cards, profile);
	const benefit = brand.benefit ? brand.benefit + "% " : "";
	const homepage = normalizeUrl(brand.homepageUrl);
	const brandNode = {
		"@type": "Brand",
		"@id": pageUrl + "#brand",
		name,
		description: stripHtml(brand.brandRmks) || undefined,
		url: homepage || undefined,
		sameAs: homepage ? [homepage] : undefined,
		telephone: brand.phone || undefined,
		logo: brand.brandPath ? imageUrl(brand.brandPath) : undefined,
	};
	const offer = {
		"@type": "Offer",
		"@id": pageUrl + "#offer",
		name: `${name} ${card} ${benefit}청구할인`,
		description: summary,
		url: pageUrl,
		category: "청구할인",
		itemOffered: { "@id": brandNode["@id"] },
		offeredBy: { "@id": MAIN_ORIGIN + "/#organization" },
		eligibleCustomerType: profile.audience || undefined,
		areaServed: { "@type": "Country", name: "KR" },
	};
	const breadcrumb = {
		"@type": "BreadcrumbList",
		"@id": pageUrl + "#breadcrumb",
		itemListElement: [
			{ "@type": "ListItem", position: 1, name: SITE_NAME, item: absoluteUrl("/") },
			{ "@type": "ListItem", position: 2, name: "제휴 브랜드", item: absoluteUrl(listPath || "/HFO_BI_001") },
			{ "@type": "ListItem", position: 3, name },
		],
	};
	const webPage = {
		"@type": "WebPage",
		"@id": pageUrl + "#webpage",
		url: pageUrl,
		name: (title || `${name} ${card} 청구할인`) + " | " + SITE_NAME,
		description: summary,
		inLanguage: "ko-KR",
		about: { "@id": brandNode["@id"] },
		mainEntity: { "@id": offer["@id"] },
		breadcrumb: { "@id": breadcrumb["@id"] },
		publisher: { "@id": MAIN_ORIGIN + "/#organization" },
	};
	setJsonLd("brand", { "@context": "https://schema.org", "@graph": [webPage, breadcrumb, brandNode, offer] });
}
